/**
 * Ambient banner shown when a newer app build is waiting to activate.
 *
 * The signal comes from the service-worker lifecycle via the {@link usePwaUpdate}
 * store (set in `../pwa.ts`). Renders through the shared {@link Banner} so it
 * matches every other banner and stacks with them at the top of the workspace —
 * replacing the old bottom-fixed, hand-styled bar. Clicking Update applies the
 * waiting worker (skipWaiting → reload); the label switches to a busy state
 * while that resolves.
 *
 * Beside the headline sits one line on what the update brings — the change
 * itself when there is one, else counts ("3 new features and 2 bugs fixed"),
 * from the new build's release notes. It never wraps: on a narrow screen it
 * takes its own line and switches to the compact form ("3 features, 2 fixes,
 * faster") when the full one does not fit, with the full text as a tooltip.
 */

import { useLayoutEffect, useRef, useState } from 'react'

import { useTranslation } from '@molecule/app-react'

import { usePwaUpdate } from '../lib/pwaUpdate.js'
import { Banner } from './Banner.js'

export function PwaUpdateBanner() {
  const { t } = useTranslation()
  const { available, update, summary } = usePwaUpdate()
  const [updating, setUpdating] = useState(false)
  const summaryRef = useRef<HTMLSpanElement>(null)
  // 'measure' renders the full line so its overflow can be read; the layout
  // effect then settles on 'full' or 'compact' before the browser paints.
  const [fit, setFit] = useState<'measure' | 'full' | 'compact'>('measure')

  useLayoutEffect(() => {
    const el = summaryRef.current
    if (fit !== 'measure' || !el) return
    setFit(el.scrollWidth > el.clientWidth ? 'compact' : 'full')
  }, [fit, summary])

  // Re-measure when the banner's width changes (rotation, resize).
  useLayoutEffect(() => {
    const banner = summaryRef.current?.closest('[data-mol-id="pwa-update-banner"]')
    if (!banner) return
    let width = banner.clientWidth
    const observer = new ResizeObserver(() => {
      if (banner.clientWidth === width) return
      width = banner.clientWidth
      setFit('measure')
    })
    observer.observe(banner)
    return () => observer.disconnect()
  }, [summary])

  if (!available) return null

  return (
    <Banner
      tone="info"
      variant="strip"
      role="alert"
      molId="pwa-update-banner"
      message={
        <>
          <span style={{ display: 'inline-block' }}>
            {t('pwa.updateAvailable', undefined, {
              defaultValue: 'New version of the Molecule IDE is available!',
            })}
          </span>
          {summary && (
            <>
              {' '}
              <span
                ref={summaryRef}
                data-mol-id="pwa-update-summary"
                title={summary.text}
                style={{
                  display: 'inline-block',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  verticalAlign: 'bottom',
                  opacity: 0.8,
                }}
              >
                {fit === 'compact' ? summary.compact : summary.text}
              </span>
            </>
          )}
        </>
      }
      actions={[
        {
          label: updating
            ? t('pwa.updating', undefined, { defaultValue: 'Updating...' })
            : t('pwa.update', undefined, { defaultValue: 'Update' }),
          // Green "go" action, preserving the prior banner's Update button intent.
          color: 'success',
          onClick: () => {
            setUpdating(true)
            update?.()
            // A successful update navigates the page away, so this never fires
            // in the normal case. It's a UI safety net only: if the worker swap
            // never completes, un-latch the button (rather than reload blindly)
            // so the user can retry instead of staring at a stuck "Updating…".
            window.setTimeout(() => setUpdating(false), 15000)
          },
          molId: 'pwa-update-button',
        },
      ]}
    />
  )
}
